import type {LatencyMode} from 'bitmovin-player';

import {State} from '../../analyticsStateMachines/bitmovin8/Bitmovin8AnalyticsStateMachine';
import {LatencyMeter} from '../../core/LatencyMeter';
import {LatencyInfo} from '../../types/LatencyInfo';
import {isFiniteNumber} from '../../utils/numberUtils';
import {calculateTime} from '../../utils/Utils';

import {Bitmovin8PlayerProxy} from './Bitmovin8PlayerProxy';
import {BITMOVIN8_LATENCY_MODE} from './player/Bitmovin8PlayerTypes';

/**
 * Minimum distance between two samples cut because the target latency changed. An application that
 * binds the target to a control emits a change per interaction step, so without it a single drag of
 * a slider, or an automation that keeps adjusting the target, would turn into dozens of samples.
 */
const MIN_INTERVAL_BETWEEN_TARGET_LATENCY_CUTS_MS = 30000;

/**
 * Feeds the {@link LatencyMeter} from the Bitmovin Player low latency API for the periods in which
 * the player steers playback towards the target latency.
 *
 * @see https://cdn.bitmovin.com/player/web/8/docs/interfaces/Core.LowLatencyAPI.html
 * @see https://bitmovin.atlassian.net/wiki/spaces/ANALYTICS/pages/3846995970/Feature+Latency
 */
export class Bitmovin8LatencyMeterService {
  private readonly player: Bitmovin8PlayerProxy;
  private readonly meter: LatencyMeter;
  /**
   * The player reports its mode only when it changes, so every period the collector cannot attribute
   * to a reported mode counts as suspended.
   */
  private isLatencyControlSuspended = true;
  private lastTargetLatencyCutTimestamp = 0;

  constructor(player: Bitmovin8PlayerProxy, meter: LatencyMeter) {
    this.player = player;
    this.meter = meter;
  }

  getInfo(): LatencyInfo | undefined {
    return this.meter.getInfo();
  }

  measure(): void {
    // Skip while the player isn't at the live edge (e.g. after a pause, seek or time shift).
    // The reported latency then reflects the viewer's chosen position, not the stream's.
    if (this.isLatencyControlSuspended) {
      return;
    }

    // during a linear ad the content stays where the break interrupted it while the live edge keeps
    // moving, so the latency would describe the break rather than the stream
    if (this.player.isLinearAdActive() === true) {
      return;
    }

    const targetLatency = this.getTargetLatencyMs();

    // without a target there is nothing the latency can be judged against
    if (targetLatency === undefined) {
      return;
    }

    const latency = this.player.getLatency();

    if (!isFiniteNumber(latency)) {
      return;
    }

    this.meter.addMeasurement(calculateTime(latency), targetLatency);
  }

  onLatencyModeChanged(latencyMode: LatencyMode): void {
    this.isLatencyControlSuspended = latencyMode === BITMOVIN8_LATENCY_MODE.Suspended;
  }

  /**
   * The player does not suspend its latency control for a time shift: it keeps reporting the
   * distance to the live edge, which after a shift is the position the viewer chose rather than one
   * of the stream. The mode stays `catchup` or `idle`, so the suspension is driven from the shift.
   */
  onTimeShifted(): void {
    const timeShift = this.player.getTimeShift();

    if (isFiniteNumber(timeShift) && timeShift !== 0) {
      this.onLatencyModeChanged(BITMOVIN8_LATENCY_MODE.Suspended);
    }
  }

  /**
   * @returns whether the running sample has to be cut, so that its measurements and the target they
   * are reported against cover the same period.
   */
  onTargetLatencyChanged(state: string, timestamp: number): boolean {
    const targetLatency = this.getTargetLatencyMs();

    if (this.shouldCutSampleOnTargetLatencyChange(state, targetLatency, timestamp)) {
      this.lastTargetLatencyCutTimestamp = timestamp;
      return true;
    }

    // a change that is not cut is taken over by the running sample, which measures against a target
    // only while playing
    if (state === State.PLAYING && targetLatency !== undefined) {
      this.meter.overrideTargetLatency(targetLatency);
    }

    return false;
  }

  private shouldCutSampleOnTargetLatencyChange(
    state: string,
    targetLatency: number | undefined,
    timestamp: number,
  ): boolean {
    // playing is the only state that can be cut, and no other one needs it: it is left before
    // playback resumes and the next sample picks up the new target on its own
    if (state !== State.PLAYING) {
      return false;
    }

    // a source that loses its target is no longer measured, and what the running sample measured so
    // far keeps the target it was measured against
    if (targetLatency === undefined) {
      return false;
    }

    return timestamp - this.lastTargetLatencyCutTimestamp >= MIN_INTERVAL_BETWEEN_TARGET_LATENCY_CUTS_MS;
  }

  resetMeasures(): void {
    this.meter.reset();
  }

  resetSourceRelatedState(): void {
    this.meter.reset();
    this.isLatencyControlSuspended = true;
    this.lastTargetLatencyCutTimestamp = 0;
  }

  /**
   * The values {@link Bitmovin8PlayerProxy.getTargetLatency} reports for "no target" are not
   * latencies.
   */
  private getTargetLatencyMs(): number | undefined {
    const targetLatency = this.player.getTargetLatency();

    if (!isFiniteNumber(targetLatency) || targetLatency <= 0) {
      return undefined;
    }

    return calculateTime(targetLatency);
  }
}
